import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import {
  Users,
  Plus,
  Trash2,
  AlertTriangle,
  Save,
  UserPlus,
  Pencil,
  UserMinus,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { CustomDropdown } from '../../components/CustomDropdown';
import { DataToolbar } from '../../components/common/DataToolbar';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { Pagination } from '../../components/common/Pagination';
import { IGroupData, IAccountDataResponse } from '@tool-no-hu/shared/types';
import {
  createGroupService,
  updateGroupService,
  deleteGroupService,
  getGroupsService,
  getGroupsPaginatedService,
} from '../../service/group';
import { getAccountsService } from '../../service/account';
import { GroupConfigPopup } from '../../components/GroupConfigPopup';
import { useConfirm } from '../../context/ConfirmContext';
import { showToast } from '../../context/ToastContext';

const GAME_BADGE_COLORS: Record<string, string> = {
  TX: 'bg-[#3b82f6]/20 text-[#3b82f6]',
  XD: 'bg-[#8b5cf6]/20 text-[#8b5cf6]',
  TL: 'bg-[#f59e0b]/20 text-[#f59e0b]',
};

/** Group giờ luôn cần đúng 4 tài khoản — server random 2/4 vào mỗi side mỗi round. */
const REQUIRED_MEMBER_COUNT = 4;

interface GroupCardProps {
  group: IGroupData;
  groups: IGroupData[];
  accounts: IAccountDataResponse[];
  onDeleteGroup: (id: string) => void;
  onUpdateGroup: (id: string, memberIds: string[]) => Promise<boolean>;
  /** Mở modal sửa nhóm — gộp tên nhóm, game và cấu hình của mọi thành viên. */
  onEditGroup?: (group: IGroupData) => void;
}

const GroupCard: React.FC<GroupCardProps> = ({
  group,
  groups,
  accounts,
  onDeleteGroup,
  onUpdateGroup,
  onEditGroup,
}) => {
  const [localMembers, setLocalMembers] = useState<IAccountDataResponse[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalMembers((group.members as IAccountDataResponse[]) || []);
  }, [group]);

  const availableAccounts = accounts.filter((acc) => {
    if (localMembers.some((m) => m.username === acc.username)) return false;

    const isInOtherGroup = groups.some(
      (g) =>
        g._id !== group._id &&
        (g.members as IAccountDataResponse[]).some((m) => m.username === acc.username),
    );
    if (isInOtherGroup) return false;

    return true;
  });

  useEffect(() => {
    if (availableAccounts.length > 0 && !selectedAccount) {
      setSelectedAccount(availableAccounts[0]?.username || '');
    } else if (availableAccounts.length === 0) {
      setSelectedAccount('');
    }
  }, [accounts, localMembers, selectedAccount, availableAccounts]);

  // Thêm/xoá thành viên chỉ đổi state local — phải bấm "Lưu" mới ghi lên server.
  // Side/cấu hình cược không còn gắn per-account nữa nên add member không cần gán gì
  // thêm — tất cả cấu hình cược (sideConfigs) giờ sửa ở popup GroupConfigPopup.
  const handleAddMemberLocally = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || saving) return;

    const acc = accounts.find((a) => a.username === selectedAccount);
    if (!acc) return;

    setLocalMembers((prev) => [...prev, acc]);
    setSelectedAccount('');
  };

  const handleRemoveMemberLocally = (username: string) => {
    if (saving) return;
    setLocalMembers((prev) => prev.filter((member) => member.username !== username));
  };

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    setSaving(true);
    const memberIds = localMembers.map((member) => member._id).filter(Boolean) as string[];
    const success = await onUpdateGroup(group._id, memberIds);
    if (success) showToast.success('Đã lưu nhóm');
    setSaving(false);
  };

  // Group cần đúng 4 tài khoản để chạy — server random 2/4 vào mỗi side mỗi round.
  const memberCountWarning =
    localMembers.length !== REQUIRED_MEMBER_COUNT
      ? `Nhóm đang có ${localMembers.length} tài khoản — cần đúng ${REQUIRED_MEMBER_COUNT} tài khoản để chơi.`
      : null;
  const isReady = memberCountWarning === null;

  const savedMemberIds = (((group.members as IAccountDataResponse[]) || [])
    .map((member) => member._id)
    .filter(Boolean) as string[]);
  const localMemberIds = localMembers.map((member) => member._id).filter(Boolean) as string[];
  const membersChanged =
    localMemberIds.length !== savedMemberIds.length ||
    localMemberIds.some((id) => !savedMemberIds.includes(id));
  const hasChanges = membersChanged;

  return (
    <div
      className={`rounded-xl border bg-[#161920] p-6 ${
        isReady
          ? 'border-[#252a38]'
          : 'border-l-[4px] border-l-[#ef4444] border-[#252a38]'
      }`}
    >
      {/* Header Row */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="text-[16px] font-extrabold text-[#e2e8f0]">{group.name}</h4>
            <span
              className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                GAME_BADGE_COLORS[group.gameType] || 'bg-[#252a38] text-[#94a3b8]'
              }`}
            >
              {group.gameType || 'TX'}
            </span>
            <button
              type="button"
              onClick={() => onEditGroup?.(group)}
              disabled={saving || membersChanged}
              title={
                membersChanged
                  ? 'Lưu thay đổi thành viên trước rồi mới cấu hình được'
                  : 'Sửa nhóm — tên, game và cấu hình cược'
              }
              className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Pencil size={13} />
            </button>
          </div>
          {memberCountWarning && (
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#ef4444]">
              <AlertTriangle size={13} className="shrink-0" />
              <span>{memberCountWarning}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => onDeleteGroup(group._id)}
          className="flex items-center gap-1.5 rounded-lg border border-[#7f1d1d] bg-[#1c0a0a] px-3 py-1.5 text-[11px] font-bold text-[#ef4444] transition-colors hover:bg-[#2d0a0a]"
        >
          <Trash2 size={12} />
          <span>Xoá nhóm</span>
        </button>
      </div>

      {/* Members table — side/config đã chuyển thành sideConfigs cấp nhóm, sửa ở popup
          GroupConfigPopup nên bảng này chỉ còn cần hiển thị danh tính + trạng thái. */}
      <div className="overflow-x-auto rounded-lg border border-[#252a38]">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-[#10131a]">
              <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                Tài khoản
              </th>
              <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                Trạng thái
              </th>
              <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                Proxy
              </th>
              <th className="px-4 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                Hành động
              </th>
            </tr>
          </thead>
          <tbody>
            {localMembers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-[12px] text-[#475569]">
                  Chưa có thành viên nào trong nhóm
                </td>
              </tr>
            ) : (
              localMembers.map((member) => {
                return (
                  <tr
                    key={member.username}
                    className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]"
                  >
                    <td className="px-4 py-3 text-[12px] font-semibold text-[#e2e8f0]">
                      {member.username}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#14532d] bg-[#0d1f14] px-2 py-0.5 text-[10px] font-bold text-[#22c55e]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
                        {member.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-[11px] text-[#94a3b8]">
                        {member.proxy ? member.proxy.split(':').slice(0, 2).join(':') : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* Cấu hình cược đã gộp vào nút sửa nhóm ở đầu card */}
                        <button
                          onClick={() => handleRemoveMemberLocally(member.username)}
                          disabled={saving || member.status === 'RUNNING'}
                          title={member.status === 'RUNNING' ? 'Cần stop trước ở trang Tài khoản' : 'Remove Member'}
                          className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1c2030] hover:text-[#ef4444] disabled:cursor-not-allowed disabled:opacity-40"
                          type="button"
                        >
                          <UserMinus size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Action Row */}
      <div className="mt-4 flex flex-col justify-between gap-3 border-t border-dashed border-[#252a38] pt-4 sm:flex-row sm:items-center">
        {/* Left: Add Member */}
        <form onSubmit={handleAddMemberLocally} className="flex items-center gap-2">
          <CustomDropdown
            value={selectedAccount}
            onChange={setSelectedAccount}
            disabled={saving || localMembers.length >= REQUIRED_MEMBER_COUNT || availableAccounts.length === 0}
            openId={`group-${group._id}-add`}
            placeholder={
              availableAccounts.length === 0
                ? '-- Không có tài khoản khả dụng --'
                : '-- Chọn tài khoản --'
            }
            className="min-w-[200px]"
            options={availableAccounts.map((acc) => ({
              value: acc.username,
              label: acc.username,
            }))}
          />
          <button
            type="submit"
            disabled={saving || !selectedAccount || localMembers.length >= REQUIRED_MEMBER_COUNT}
            title={
              localMembers.length >= REQUIRED_MEMBER_COUNT
                ? `Nhóm đã đủ ${REQUIRED_MEMBER_COUNT} tài khoản`
                : undefined
            }
            className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:bg-[#1e2235] disabled:text-[#334155]"
          >
            <UserPlus size={13} />
            <span>Thêm tài khoản</span>
          </button>
        </form>

        {/* Right: Save */}
        <form
          onSubmit={handleSaveGroup}
          className="flex items-center gap-2 text-[12px] font-bold text-[#e2e8f0]"
        >
          <button
            type="submit"
            disabled={saving || !hasChanges}
            title={hasChanges ? 'Lưu thay đổi' : 'Chưa có thay đổi nào'}
            className="ml-1 flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3b82f6] px-4 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:bg-[#1e2235] disabled:text-[#334155]"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            <span>{saving ? 'Đang lưu...' : 'Lưu'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export const Group: React.FC = () => {
  const [accounts, setAccounts] = useState<IAccountDataResponse[]>([]);
  const [groups, setGroups] = useState<IGroupData[]>([]);
  const [groupEditTarget, setGroupEditTarget] = useState<IGroupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const confirm = useConfirm();

  // Debounce search — tự động search sau 400ms không gõ
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute pagination pages
  const pageNumbers: number[] = [];
  if (totalPages > 1) {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    for (let i = 0; i < 5; i++) {
      const p = start + i;
      if (p > totalPages) break;
      pageNumbers.push(p);
    }
  }

  const fetchAccounts = async () => {
    try {
      const data = await getAccountsService('LIST_DATA');
      setAccounts(data as IAccountDataResponse[]);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const result = await getGroupsPaginatedService(page, 10, search || undefined);
      setGroups(result.items);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupGameType, setNewGroupGameType] = useState<string>('TX');
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || creating) return;
    setCreating(true);
    try {
      const group = await createGroupService(newGroupName, newGroupGameType);
      if (group) {
        setNewGroupName('');
        setNewGroupGameType('TX');
        setShowCreatePopup(false);
        // Optimistic — prepend new group to list
        setGroups((prev) => [group, ...prev]);
      } else {
        showToast.error('Tạo nhóm thất bại');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async (id: string) => {
    const isConfirmed = await confirm({
      title: 'Xoá nhóm',
      message: 'Bạn có chắc chắn muốn xoá nhóm này?',
      type: 'danger',
    });
    if (isConfirmed) {
      const success = await deleteGroupService(id);
      if (success) {
        // Optimistic — filter from list
        setGroups((prev) => prev.filter((g) => g._id !== id));
      } else {
        showToast.error('Xoá nhóm thất bại');
      }
    }
  };

  const handleUpdateGroup = async (id: string, memberIds: string[]): Promise<boolean> => {
    const updated = await updateGroupService(id, {
      members: memberIds,
    });
    if (updated) {
      setGroups((prev) =>
        prev.map((g) => {
          if (g._id !== id) return g;

          const currentMembers = g.members as IAccountDataResponse[];
          const hydratedMembers = memberIds
            .map((memberId) => {
              const existingMember = currentMembers.find((member) => member._id === memberId);
              if (existingMember) return existingMember;
              return accounts.find((account) => account._id === memberId) || null;
            })
            .filter(Boolean) as IAccountDataResponse[];

          return {
            ...g,
            members: hydratedMembers,
          };
        }),
      );
      return true;
    }

    showToast.error('Lưu cấu hình nhóm thất bại');
    return false;
  };

  return (
    <div className="space-y-4 text-[#e2e8f0]">
      {/* Page Header */}
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
          <Users size={20} className="text-[#3b82f6]" />
        </div>
        <div>
          <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">Nhóm</h1>
          <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
            Chọn đúng 4 tài khoản cho mỗi nhóm — cấu hình cược theo side (Chẵn/Lẻ) sửa ở nút
            "Sửa nhóm"
          </p>
        </div>
      </div>

      {/* Top Card — Create Group (replaced with button + popup) */}

      {/* Group Cards List */}
      <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
        <DataToolbar
          icon={<Users size={16} />}
          title="Danh sách nhóm"
          count={total}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Tìm nhóm hoặc tên tài khoản..."
          action={
            <button
              type="button"
              onClick={() => {
                setNewGroupName('');
                setShowCreatePopup(true);
              }}
              className="ml-2 flex items-center gap-1 rounded-lg bg-[#3b82f6] px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-[#2563eb]"
            >
              <Plus size={13} />
              Tạo nhóm
            </button>
          }
        />

        <DataStateMessage
          loading={loading}
          empty={!loading && groups.length === 0 ? (search ? 'Không tìm thấy nhóm phù hợp' : 'Chưa có nhóm nào') : null}
          emptyHint={
            !loading && groups.length === 0
              ? search
                ? 'Thử tìm kiếm với từ khóa khác'
                : 'Điền tên nhóm ở ô phía trên và nhấn "Tạo nhóm" để bắt đầu'
              : undefined
          }
          emptyIcon={<Users size={32} className="text-[#252a38]" />}
        />

        {!loading && groups.length > 0 && (
          <div className="space-y-4 p-5">
            {groups.map((group) => (
              <GroupCard
                key={group._id}
                group={group}
                groups={groups}
                accounts={accounts}
                onDeleteGroup={handleDeleteGroup}
                onUpdateGroup={handleUpdateGroup}
                onEditGroup={(grp) => setGroupEditTarget(grp)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-[#252a38] px-5 py-3">
          <span className="text-[12px] text-[#64748b]">
            Tổng: <strong className="text-[#e2e8f0]">{total}</strong> nhóm
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-1.5 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={14} />
              <span className="hidden sm:inline">Trước</span>
            </button>

            {pageNumbers.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`min-w-[32px] rounded-lg px-2.5 py-1.5 text-[12px] font-bold transition-colors ${
                  p === page
                    ? 'bg-[#3b82f6] text-white'
                    : 'border border-[#252a38] bg-[#0d0f14] text-[#94a3b8] hover:bg-[#1e2235] hover:text-[#e2e8f0]'
                }`}
              >
                {p}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="flex items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3 py-1.5 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="hidden sm:inline">Sau</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Group Config Popup — tên nhóm, game và cấu hình mọi thành viên */}
      {groupEditTarget && (
        <GroupConfigPopup
          isOpen={!!groupEditTarget}
          onClose={() => setGroupEditTarget(null)}
          group={groupEditTarget}
          onSaved={async () => {
            // PUT trả về group thô (members chưa enrich) nên refetch để bảng có lại
            // username/proxy/config string.
            await Promise.all([fetchAccounts(), fetchGroups()]);
          }}
        />
      )}

      {/* Create Group Popup */}
      <AnimatePresence>
        {showCreatePopup && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/60" onClick={() => !creating && setShowCreatePopup(false)} />
            <motion.div
              className="relative w-full max-w-[420px] rounded-xl border border-[#252a38] bg-[#161920] shadow-2xl"
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-[#252a38] px-5 py-4">
                <h3 className="text-[14px] font-bold text-[#e2e8f0]">Tạo nhóm mới</h3>
                <button
                  onClick={() => !creating && setShowCreatePopup(false)}
                  disabled={creating}
                  className="rounded-lg p-1.5 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4 px-5 pt-5">
                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                    Tên nhóm
                  </label>
                  <input
                    type="text"
                    autoFocus
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !creating && newGroupName.trim()) handleCreateGroup();
                    }}
                    placeholder="Điền tên nhóm..."
                    className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                    Game
                  </label>
                  <div className="inline-flex rounded-lg border border-[#252a38] bg-[#0d0f14] p-0.5">
                    {(['TX', 'XD', 'TL'] as const).map((gt) => (
                      <button
                        key={gt}
                        type="button"
                        onClick={() => setNewGroupGameType(gt)}
                        className={`rounded-md px-3 py-1.5 text-[12px] font-bold transition-all ${
                          newGroupGameType === gt
                            ? 'bg-[#3b82f6] text-white shadow-sm'
                            : 'text-[#64748b] hover:text-[#e2e8f0]'
                        }`}
                      >
                        {gt === 'TX' ? 'Tài Xỉu' : gt === 'XD' ? 'Xóc Đĩa' : 'Tứ Linh'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-[#252a38] px-5 py-4 mt-5">
                <button
                  onClick={() => setShowCreatePopup(false)}
                  disabled={creating}
                  className="rounded-lg border border-[#252a38] px-4 py-2 text-[12px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={creating || !newGroupName.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2 text-[12px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creating && <Loader2 size={14} className="animate-spin" />}
                  {creating ? 'Đang tạo...' : 'Tạo nhóm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};